import React, { useEffect } from 'react';
import * as S from './Modal.styled';
import { useAnimatedVisibility } from '@/hooks/useAnimatedVisibility';

interface ModalProps {
  isOpen: boolean;
  maxWidth?: number;
  borderRadius?: number;
  onClose?: () => void;
  isScrollLocked?: boolean;
  dataTestId?: string;
  className?: string;
  children: React.ReactNode;
  background?: string;
}

export const Modal = ({
  isOpen,
  onClose,
  children,
  maxWidth = 460,
  borderRadius = 14,
  dataTestId,
  isScrollLocked = false,
  className,
  background,
}: ModalProps) => {
  const { isVisible, onAnimationEnd } = useAnimatedVisibility(isOpen);

  useEffect(() => {
    if (isScrollLocked) {
      document.body.style.overflow = isOpen ? 'hidden' : 'auto';
    }
  }, [isOpen, isScrollLocked]);

  if (!isVisible) return null;

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onClose?.();
  };

  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <S.ModalWrapper
      data-testid={dataTestId}
      onAnimationEnd={onAnimationEnd}
      $isOpen={isOpen}
    >
      <S.Backdrop
        onClick={handleClose}
        $isOpen={isOpen}
        data-testid="modal-backdrop"
        className="reveal-modal-bg"
      />
      <S.ModalContent
        $isOpen={isOpen}
        $maxWidth={maxWidth}
        $borderRadius={borderRadius}
        onClick={handleContentClick}
        data-testid="modal-content"
        className={className}
        $background={background}
      >
        {children}
      </S.ModalContent>
    </S.ModalWrapper>
  );
};
